/**
 * Breadcrumb utility functions
 */
import { normalizeTurkish } from "./stringUtils";
import { getLocalizedPath } from "@/utils/pathUtils";

/**
 * Generate dynamic breadcrumb items for bus pages based on page type and data
 */

export function generatePagesBreadcrumb(
  pageType,
  translationData,
  t,
  namespace = "common",
  locale = "tr",
) {
  const baseItems = [
    {
      label: "Turna",
      href: getLocalizedPath("/", locale),
      isActive: false,
    },
  ];

  switch (pageType) {
    case "Reservation":
      return [
        ...baseItems,
        {
          label: t(`${namespace}.busReservationDetailBreadcrumbLabel`),
          href: "",
        },
        {
          label: t(`${namespace}.reservationDetailBreadcrumbLabel`),
          href: null,
          isActive: true,
        },
      ];
    default:
      return baseItems;
  }
}

export function generateBusPageBreadcrumb(
  pageType,
  translationData,
  t,
  namespace = "bus",
  locale = "tr",
) {
  const { origin, destination, company, station } = translationData;

  const baseItems = [
    {
      label: "Turna",
      href: getLocalizedPath("/", locale),
      isActive: false,
    },
    {
      label: t(`${namespace}.busTicketBreadCrumpLabel`),
      href: getLocalizedPath("/bus-ticket", locale),
      isActive: false,
    },
  ];

  switch (pageType) {
    case "BusTripRoute":
      return [
        ...baseItems,
        {
          label: `${destination} ${t(`${namespace}.busTicketBreadCrumpLabel`)}`,
          href: `${getLocalizedPath(
            "/bus-ticket",
            locale,
          )}/${destination.toLowerCase()}`,
          isActive: false,
        },
        {
          label: `${origin}-${destination}`,
          href: null,
          isActive: true,
        },
      ];

    case "City":
      return [
        ...baseItems,
        {
          label: origin,
          href: null,
          isActive: true,
        },
      ];

    case "Company":
      return [
        ...baseItems,
        {
          label: company,
          href: null,
          isActive: true,
        },
      ];

    case "BusTerminal":
      return [
        ...baseItems,
        {
          label: t(`${namespace}.busTicketBreadCrumpStationListLabel`),
          href: getLocalizedPath("/bus-ticket/bus-terminals", locale),
          isActive: false,
        },
        {
          label: t(`${namespace}.busTicketBreadCrumpStationLabel`, { station }),
          href: null,
          isActive: true,
        },
      ];

    case "Homepage":
      return [
        {
          label: "Turna",
          href: getLocalizedPath("/", locale),
          isActive: false,
        },
        {
          label: t(`${namespace}.busTicketBreadCrumpLabel`),
          href: null,
          isActive: true,
        },
      ];

    default:
      return baseItems;
  }
}

/**
 * Generate dynamic breadcrumb items for flight pages based on page type and data
 * Uses getLocalizedPath for i18n URLs; last item has href: null, isActive: true
 */
function toSlug(str) {
  if (!str) return "";
  return str
    .toString()
    .toLowerCase()
    .replace(/ğ/g, "g")
    .replace(/ü/g, "u")
    .replace(/ş/g, "s")
    .replace(/ı/g, "i")
    .replace(/ö/g, "o")
    .replace(/ç/g, "c")
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();
}

export function generateFlightPageBreadcrumb(
  pageType,
  translationData,
  t,
  namespace = "breadcrumb",
  locale = "tr",
) {
  const data = translationData || {};
  const { origin, destination, city, airport, airline, country, continent } =
    data;

  const baseItems = [
    {
      label: t ? t(`${namespace}.brandName`) : "Turna",
      href: getLocalizedPath("/", locale),
      isActive: false,
    },
    {
      label: t ? t(`${namespace}.flightTicket`) : "Uçak Bileti",
      href: getLocalizedPath("/flight", locale),
      isActive: false,
    },
  ];

  const type = (pageType || "").toString().toLowerCase().replace(/\s+/g, "");

  switch (type) {
    case "homepage":
      return [
        {
          label: t ? t(`${namespace}.brandName`) : "Turna",
          href: getLocalizedPath("/", locale),
          isActive: false,
        },
        {
          label: t ? t(`${namespace}.flightTicket`) : "Uçak Bileti",
          href: null,
          isActive: true,
        },
      ];

    case "flightroute":
      if (destination) {
        return [
          ...baseItems,
          {
            label: `${destination} ${t ? t(`${namespace}.flightTicket`) : "Uçak Bileti"}`,
            href: `${getLocalizedPath("/flight", locale)}/${toSlug(destination)}`,
            isActive: false,
          },
          {
            label: [origin, destination].filter(Boolean).join(" - ") || "",
            href: null,
            isActive: true,
          },
        ];
      }
      return [
        ...baseItems,
        {
          label: [origin, destination].filter(Boolean).join(" - ") || "",
          href: null,
          isActive: true,
        },
      ];

    case "city":
      return [
        ...baseItems,
        {
          label: city || "",
          href: `${getLocalizedPath("/flight", locale)}/${toSlug(city)}`,
          isActive: true,
        },
      ];

    case "airport":
      return [
        ...baseItems,
        {
          label: airport || "",
          href: `${getLocalizedPath("/flight", locale)}/${toSlug(airport)}`,
          isActive: true,
        },
      ];

    case "airline":
    case "flightcompany":
      return [
        ...baseItems,
        {
          label: airline || "",
          href: `${getLocalizedPath("/flight", locale)}/${toSlug(airline)}`,
          isActive: true,
        },
      ];

    case "airlinepromotions":
      return [
        ...baseItems,
        {
          label:
            (t ? t(`${namespace}.airlinePromotions`) : null) ||
            "Özel Kampanyalar",
          href: null,
          isActive: true,
        },
      ];

    case "country":
      return [
        ...baseItems,
        {
          label: country || "",
          href: `${getLocalizedPath("/flight", locale)}/${toSlug(country)}`,
          isActive: true,
        },
      ];

    case "continent":
      return [
        ...baseItems,
        {
          label: continent || "",
          href: `${getLocalizedPath("/flight", locale)}/${toSlug(continent)}`,
          isActive: true,
        },
      ];

    case "skyscanner":
      return [
        ...baseItems,
        { label: airline || "SkyScanner", href: null, isActive: true },
      ];

    case "allairlines":
      return [
        ...baseItems,
        {
          label: (t ? t(`${namespace}.allAirlines`) : null) || "Uçak Firmaları",
          href: null,
          isActive: true,
        },
      ];

    case "allairports":
      return [
        ...baseItems,
        {
          label: (t ? t(`${namespace}.allAirports`) : null) || "Havalimanları",
          href: null,
          isActive: true,
        },
      ];

    case "commercial":
      return [
        ...baseItems,
        {
          label: t ? t(`${namespace}.umreBreadcrumbParentTitle`) : "",
          href: t ? t(`${namespace}.umreBreadcrumbParentHref`) : "",
          isActive: true,
        },
        {
          label: (t ? t(`${namespace}.umreBreadcrumbTitle`) : null) || "",
          href: (t ? t(`${namespace}.umreBreadcrumbHref`) : null) || "",
          isActive: true,
        },
      ];

    case "internationalflights":
    case "internationalflight":
    case "international_flight_ticket":
      return [
        ...baseItems,
        {
          label:
            (t ? t(`${namespace}.internationalFlightTicket`) : null) ||
            "Yurtdışı Uçak Bileti",
          href: null,
          isActive: true,
        },
      ];

    case "domesticflights":
    case "domesticflight":
    case "domestic_flight_ticket":
      return [
        ...baseItems,
        {
          label:
            (t ? t(`${namespace}.domesticFlightTicket`) : null) ||
            "Yurtiçi Uçak Bileti",
          href: null,
          isActive: true,
        },
      ];

    case "promotion":
    case "promotionsmain":
    case "specialpromotions":
      return [
        ...baseItems,
        {
          label: (t ? t(`${namespace}.promotions`) : null) || "Kampanyalar",
          href: null,
          isActive: true,
        },
      ];

    default:
      return baseItems;
  }
}

export function generateFerryPageBreadcrumb(
  pageType,
  translationData,
  t,
  namespace = "ferry",
  locale = "tr",
) {
  const { origin, destination, company, station } = translationData;
  const baseItems = [
    {
      label: "Turna",
      href: getLocalizedPath("/", locale),
      isActive: false,
    },
    {
      label: t(`${namespace}.ferryTicketBreadCrumpLabel`),
      href: getLocalizedPath("/ferry", locale),
      isActive: false,
    },
  ];

  switch (pageType) {
    case "FerryRoute":
      return [
        ...baseItems,
        {
          label: `${destination} ${t(`${namespace}.ferryTicketBreadCrumpLabel`)}`,
          href: `${getLocalizedPath(
            "/ferry",
            locale,
          )}/${normalizeTurkish(destination).toLowerCase()}`,
          isActive: false,
        },
        {
          label: `${origin}-${destination}`,
          href: null,
          isActive: true,
        },
      ];

    case "FerryLocation":
      return [
        ...baseItems,
        {
          label: origin,
          href: null,
          isActive: true,
        },
      ];

    case "FerryCompanyList":
      return [
        ...baseItems,
        {
          label: company,
          href: null,
          isActive: true,
        },
      ];

    case "FerryGreekIslands":
      return [
        ...baseItems,
        {
          label: t(`${namespace}.greekIslandsBreadCrumpLabel`),
          href: null,
          isActive: true,
        },
      ];

    case "FerryCyprus":
      return [
        ...baseItems,
        {
          label: t(`${namespace}.cyprusBreadCrumpLabel`),
          href: null,
          isActive: true,
        },
      ];

    case "FerryPort":
      return [
        ...baseItems,
        {
          label: t(`${namespace}.portBreadCrumpLabel`, { origin }),
          href: null,
          isActive: true,
        },
      ];
    case "FerryCompany":
      return [
        ...baseItems,
        {
          label: company,
          href: null,
          isActive: true,
        },
      ];

    // Legacy cases for backward compatibility
    case "FerryTripRoute":
      return [
        ...baseItems,
        {
          label: `${destination} ${t(`${namespace}.ferryTicketBreadCrumpLabel`)}`,
          href: `${getLocalizedPath(
            "/ferry",
            locale,
          )}/${destination.toLowerCase()}`,
          isActive: false,
        },
        {
          label: `${origin}-${destination}`,
          href: null,
          isActive: true,
        },
      ];

    case "City":
      return [
        ...baseItems,
        {
          label: origin,
          href: null,
          isActive: true,
        },
      ];

    case "Company":
      return [
        ...baseItems,
        {
          label: company,
          href: null,
          isActive: true,
        },
      ];

    case "Terminal":
      return [
        ...baseItems,
        {
          label: station,
          href: null,
          isActive: true,
        },
      ];

    case "Homepage":
      return [
        {
          label: "Turna",
          href: getLocalizedPath("/", locale),
          isActive: false,
        },
        {
          label: t(`${namespace}.ferryTicketBreadCrumpLabel`),
          href: null,
          isActive: true,
        },
      ];

    default:
      return baseItems;
  }
}

export function generateHotelPageBreadcrumb(
  pageType,
  translationData,
  t,
  namespace = "hotel",
  locale = "tr",
) {
  const translate = (suffix, fallback) => {
    if (!t) return fallback;
    try {
      return t(`${namespace}.${suffix}`);
    } catch {
      return fallback;
    }
  };

  const homeLabel = translate("breadcrumbLabel", "Otel Rezervasyonu");

  const rootItem = {
    label: "Turna",
    href: getLocalizedPath("/", locale),
    isActive: false,
  };

  switch (pageType) {
    case "Homepage":
      return [
        rootItem,
        {
          label: homeLabel,
          href: null,
          isActive: true,
        },
      ];

    default:
      return [
        rootItem,
        {
          label: homeLabel,
          href: getLocalizedPath("/hotel", locale),
          isActive: false,
        },
      ];
  }
}

export function generateCarPageBreadcrumb(
  pageType,
  translationData,
  t,
  namespace = "Car",
  locale = "tr",
) {
  const { location } = translationData;

  const translate = (suffix, fallback) => {
    if (!t) return fallback;
    try {
      return t(`${namespace}.${suffix}`);
    } catch {
      return fallback;
    }
  };

  const homeLabel = translate("breadcrumbLabel", "Araç Kiralama");

  const rootItem = {
    label: "Turna",
    href: getLocalizedPath("/", locale),
    isActive: false,
  };

  switch (pageType) {
    case "Homepage":
      return [
        rootItem,
        {
          label: homeLabel,
          href: null,
          isActive: true,
        },
      ];

    case "CompanyListing":
      return [
        rootItem,
        {
          label: homeLabel,
          href: getLocalizedPath("/car-rental", locale),
          isActive: false,
        },
        {
          label: translate("CarRentalCompanies", "Araç Firmaları"),
          href: getLocalizedPath("/car-rental/company", locale),
          isActive: true,
        },
      ];

    case "City":
      return [
        rootItem,
        {
          label: homeLabel,
          href: getLocalizedPath("/car-rental", locale),
          isActive: false,
        },
        {
          label: location?.Name,
          isActive: true,
        },
      ];

    case "District":
      return [
        rootItem,
        {
          label: homeLabel,
          href: getLocalizedPath("/car-rental", locale),
          isActive: false,
        },
        {
          label: location?.City,
          href: getLocalizedPath("/car-rental/city/[slug]", locale).replace(
            "[slug]",
            location?.ParentSlug,
          ),
          isActive: true,
        },
        {
          label: location?.Name,
          isActive: true,
        },
      ];

    case "Airport":
      return [
        rootItem,
        {
          label: homeLabel,
          href: getLocalizedPath("/car-rental", locale),
          isActive: false,
        },
        {
          label: location?.City,
          href: getLocalizedPath("/car-rental/city/[slug]", locale).replace(
            "[slug]",
            location?.ParentSlug,
          ),
          isActive: true,
        },
        {
          label: location?.Name,
          isActive: true,
        },
      ];

    case "CompanyDetail":
      return [
        rootItem,
        {
          label: homeLabel,
          href: getLocalizedPath("/car-rental", locale),
          isActive: false,
        },
        {
          label: translate("CarRentalCompanies", "Araç Firmaları"),
          href: getLocalizedPath("/car-rental/company", locale),
          isActive: true,
        },
        {
          label: location?.Name,
          isActive: true,
        },
      ];

    case "Landing":
      return [
        rootItem,
        {
          label: homeLabel,
          href: getLocalizedPath("/car-rental", locale),
          isActive: false,
        },
        {
          label: location?.label || "",
          href: location?.href || "#",
          isActive: true,
        },
      ];

    default:
      return [
        rootItem,
        {
          label: homeLabel,
          href: getLocalizedPath("/car-rental", locale),
          isActive: false,
        },
      ];
  }
}

export function generateCampaignsPageBreadcrumb(
  pageType,
  campaignData,
  t,
  locale = "tr",
) {
  const rootItem = {
    label: "Turna",
    href: getLocalizedPath("/", locale),
    isActive: false,
  };

  if (pageType === "allCampaigns") {
    return [rootItem, { label: "Kampanyalar", href: null, isActive: true }];
  } else if (pageType === "flight") {
    return [
      rootItem,
      {
        label: "Kampanyalar",
        href: getLocalizedPath("/campaigns", locale),
        isActive: false,
      },
      { label: "Uçak Kampanyaları", href: null, isActive: true },
    ];
  } else if (pageType === "bus") {
    return [
      rootItem,
      {
        label: "Kampanyalar",
        href: getLocalizedPath("/campaigns", locale),
        isActive: false,
      },
      { label: "Otobüs Kampanyaları", href: null, isActive: true },
    ];
  } else if (pageType === "ferry") {
    return [
      rootItem,
      {
        label: "Kampanyalar",
        href: getLocalizedPath("/campaigns", locale),
        isActive: false,
      },
      { label: "Feribot Kampanyaları", href: null, isActive: true },
    ];
  } else if (pageType === "hotel") {
    return [
      rootItem,
      {
        label: "Kampanyalar",
        href: getLocalizedPath("/campaigns", locale),
        isActive: false,
      },
      { label: "Otel Kampanyaları", href: null, isActive: true },
    ];
  } else if (pageType === "car") {
    return [
      rootItem,
      {
        label: "Kampanyalar",
        href: getLocalizedPath("/campaigns", locale),
        isActive: false,
      },
      { label: "Araç Kampanyaları", href: null, isActive: true },
    ];
  } else if (pageType === "campaignDetail") {
    return [
      rootItem,
      {
        label: "Kampanyalar",
        href: getLocalizedPath("/campaigns", locale),
        isActive: false,
      },
      {
        label: campaignData.title,
        href: getLocalizedPath(`/campaigns/${campaignData.Slug}`, locale),
        isActive: true,
      },
    ];
  }

  return [rootItem];
}
